import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { api, formatAuthError, storeAuthTokens, clearAuthTokens } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading, false = anonymous, obj = user
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      clearAuthTokens();
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  useEffect(() => {
    const onCleared = () => {
      setUser(false);
      setLoading(false);
    };
    window.addEventListener("matpass:auth-cleared", onCleared);
    return () => window.removeEventListener("matpass:auth-cleared", onCleared);
  }, []);

  const login = useCallback(async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      storeAuthTokens(data);
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatAuthError(e) };
    }
  }, []);

  /** Connexion back-office via POST /admin/login (refuse les non-admin). */
  const adminLogin = useCallback(async (email, password) => {
    try {
      const { data } = await api.post("/admin/login", { email, password });
      if (!data?.access_token) {
        console.error("[adminLogin] pas de JWT dans la réponse", data);
        return { ok: false, error: "Erreur interne" };
      }
      storeAuthTokens(data);
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatAuthError(e) };
    }
  }, []);

  const register = useCallback(async (payload) => {
    try {
      const { data } = await api.post("/auth/register", payload);
      storeAuthTokens(data);
      setUser(data.user);
      return {
        ok: true,
        email_sent: data.email_sent === true,
        email_warning: data.email_warning || null,
      };
    } catch (e) {
      return { ok: false, error: formatAuthError(e) };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    clearAuthTokens();
    setUser(false);
  }, []);

  const value = useMemo(
    () => ({ user, loading, login, adminLogin, register, logout, refreshUser: fetchMe }),
    [user, loading, login, adminLogin, register, logout, fetchMe],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
